/**
 * #447 — runtime configuration reader.
 *
 * A single, env-agnostic container image serves every environment. Per-env
 * values (e.g. the Foundry agent-host URL) are injected at container start into
 * `window.__ENV__` by `docker-entrypoint.d/30-env-config.sh`, which writes
 * `public/env-config.js` from the container's `AGENT_HOST_URL` env var. The
 * bundle loads `/env-config.js` (classic script) before the deferred module, so
 * `window.__ENV__` is populated by the time app code reads it.
 *
 * Precedence: runtime `window.__ENV__` first, then the build-time
 * `import.meta.env.VITE_*` fallback (so local `npm run dev` / tests still work
 * without a running container), then empty string.
 */

/** Runtime environment shape injected by the container entrypoint. */
export interface RuntimeEnv {
  /** Foundry agent-host base URL for this environment. */
  AGENT_HOST_URL?: string;
  /** Golden-source (IQ structured-read) base URL for this environment (#424 M2). */
  GOLDEN_SOURCE_URL?: string;
  /** Feature gate for live Foundry threads (#424 M3): "true" enables. */
  FOUNDRY_THREADS_ENABLED?: string;
  /** Agent-host OBO scope for MSAL bearer acquisition (#424 M5); empty = no bearer. */
  AGENT_HOST_SCOPE?: string;
  /** Sprint A — MSAL application (client) id for this environment; empty = demo (no sign-in). */
  MSAL_CLIENT_ID?: string;
  /** Sprint A — MSAL tenant id (MngEnvMCAP164444, ADR-0012). */
  MSAL_TENANT_ID?: string;
  /** Sprint A — SPA redirect URI for this slot (e.g. https://appsit.curavias.ch). */
  MSAL_REDIRECT_URI?: string;
  /** Sprint A — deployment env (dev|sit|prod) used when the token omits the `env` claim. */
  APP_ENV?: string;
  /** Sprint A — home hospital (usz|luks|zollikerberg|aggregated) used when the token omits `hospital`. */
  APP_HOME_HOSPITAL?: string;
  /** Sprint 41 — dedicated Product Owner Agent service URL; empty = mock fallback. */
  PO_AGENT_URL?: string;
}

declare global {
  interface Window {
    __ENV__?: RuntimeEnv;
  }
}

function runtimeEnv(): RuntimeEnv {
  if (typeof window !== 'undefined' && window.__ENV__) {
    return window.__ENV__;
  }
  return {};
}

/**
 * Resolve the Foundry agent-host base URL: runtime-injected value first, then the
 * build-time `VITE_AGENT_HOST_URL` fallback, then empty (=> built-in mock).
 */
export function getAgentHostUrl(): string {
  const runtime = runtimeEnv().AGENT_HOST_URL;
  if (runtime && runtime.length > 0) {
    return runtime;
  }
  return import.meta.env.VITE_AGENT_HOST_URL ?? '';
}

/**
 * Resolve the golden-source (IQ structured-read) base URL: runtime-injected
 * value first, then the build-time `VITE_GOLDEN_SOURCE_URL` fallback, then empty
 * (=> the read path stays simulated / degrades loud). #424 M2.
 */
export function getGoldenSourceUrl(): string {
  const runtime = runtimeEnv().GOLDEN_SOURCE_URL;
  if (runtime && runtime.length > 0) {
    return runtime;
  }
  return import.meta.env.VITE_GOLDEN_SOURCE_URL ?? '';
}

/**
 * Resolve the Foundry-threads feature gate (#424 M3): runtime-injected value
 * first (`window.__ENV__.FOUNDRY_THREADS_ENABLED`), then the build-time
 * `VITE_FOUNDRY_THREADS_ENABLED` fallback, then `false`. Enables the live
 * `(user x agent)` thread minter; when off, the drawer stays on the simulated
 * thread path (honest `simulated` provenance).
 */
export function getFoundryThreadsEnabled(): boolean {
  const runtime = runtimeEnv().FOUNDRY_THREADS_ENABLED;
  if (runtime && runtime.length > 0) {
    return runtime === 'true';
  }
  return (import.meta.env.VITE_FOUNDRY_THREADS_ENABLED ?? '') === 'true';
}

/**
 * Resolve the agent-host OBO scope (#424 M5): runtime-injected value first
 * (`window.__ENV__.AGENT_HOST_SCOPE`), then the build-time `VITE_AGENT_HOST_SCOPE`
 * fallback, then empty. When empty (SIT default) the app attaches no bearer and
 * stays byte-parity with M4 (simulated/native path). When set, identity-aware IQ
 * calls acquire an MSAL token for this scope so the agent-host can perform the
 * on-behalf-of exchange (ADR-0057). Config, not code.
 */
export function getAgentHostScope(): string {
  const runtime = runtimeEnv().AGENT_HOST_SCOPE;
  if (runtime && runtime.length > 0) {
    return runtime;
  }
  return import.meta.env.VITE_AGENT_HOST_SCOPE ?? '';
}

/** Resolve the MSAL client id: runtime value first, then build-time `VITE_MSAL_CLIENT_ID`, then empty (=> demo, no sign-in). Sprint A. */
export function getMsalClientId(): string {
  const runtime = runtimeEnv().MSAL_CLIENT_ID;
  if (runtime && runtime.length > 0) {
    return runtime;
  }
  return import.meta.env.VITE_MSAL_CLIENT_ID ?? '';
}

/** Resolve the MSAL tenant id: runtime first, then `VITE_MSAL_TENANT_ID`, then `common`. Sprint A. */
export function getMsalTenantId(): string {
  const runtime = runtimeEnv().MSAL_TENANT_ID;
  if (runtime && runtime.length > 0) {
    return runtime;
  }
  return import.meta.env.VITE_MSAL_TENANT_ID ?? 'common';
}

/** Resolve the SPA redirect URI: runtime first, then `VITE_MSAL_REDIRECT_URI`, then the current origin. Sprint A. */
export function getMsalRedirectUri(): string {
  const runtime = runtimeEnv().MSAL_REDIRECT_URI;
  if (runtime && runtime.length > 0) {
    return runtime;
  }
  const fallback = import.meta.env.VITE_MSAL_REDIRECT_URI ?? '';
  if (fallback.length > 0) {
    return fallback;
  }
  return typeof window !== 'undefined' ? window.location.origin : '';
}

/** Resolve the deployment env used when the ID token omits the `env` claim: runtime `APP_ENV` first, then `VITE_APP_ENV`, then empty. Sprint A. */
export function getAppEnv(): string {
  const runtime = runtimeEnv().APP_ENV;
  if (runtime && runtime.length > 0) {
    return runtime;
  }
  return import.meta.env.VITE_APP_ENV ?? '';
}

/** Resolve the home hospital used when the ID token omits the `hospital` claim: runtime `APP_HOME_HOSPITAL` first, then `VITE_APP_HOME_HOSPITAL`, then empty. Sprint A. */
export function getHomeHospital(): string {
  const runtime = runtimeEnv().APP_HOME_HOSPITAL;
  if (runtime && runtime.length > 0) {
    return runtime;
  }
  return import.meta.env.VITE_APP_HOME_HOSPITAL ?? '';
}

/**
 * Resolve the dedicated po-agent-service base URL: runtime-injected value
 * first, then the build-time `VITE_PO_AGENT_URL` fallback, then empty
 * (=> invokeAgent keeps using the deterministic mock for product-owner-agent).
 * Sprint 41.
 */
export function getPoAgentUrl(): string {
  const runtime = runtimeEnv().PO_AGENT_URL;
  if (runtime && runtime.length > 0) {
    return runtime;
  }
  return import.meta.env.VITE_PO_AGENT_URL ?? '';
}
